import { Component, OnInit, DestroyRef, inject, ViewChild } from '@angular/core';
import { ParamService } from '@monsido/core/param/param.service';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { AVAILABLE_PAGE_SIZE, DEFAULT_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { TranslateModule } from '@client/app/modules/translate.module';
import { MonEventTriggerModule } from '@monsido/angular-shared-components';
import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { MenuModule } from 'primeng/menu';
import { ScanExclusionsService } from '../../services/scan-exclusions.service';
import { ScanExclusionsRepo } from '../../services/scan-exclusions.repo';
import { AccessibilityScanExclusionsRepo } from '../../services/accessibility-scan-exclusions.repo';
import { ScanExclusion, Module } from '../../types/scan-exclusions.type';
import { StateService } from '@uirouter/core';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import {
    TableWrapperComponent,
} from '@client/app/shared-modules/table/components/table-wrapper.component';
import { TableCellTemplateDirective } from '@client/app/shared-modules/table/directives/table-cell-template.directive';

type ComputedScanExclusion = ScanExclusion & {
    name: string;
    actionItems: MenuItem[];
};

@Component({
    selector: 'mon-admin-scan-exclusions',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        TabsModule,
        TableModule,
        TranslateModule,
        MonEventTriggerModule,
        ConfirmDialog,
        MenuModule,
        TranslocoModule,
        PageLayoutComponent,
        TableWrapperComponent,
        TableCellTemplateDirective,
    ],
    providers: [ConfirmationService, ScanExclusionsService],
    templateUrl: './admin-scan-exclusions.component.html',
    styleUrl: './admin-scan-exclusions.component.scss',
})
export class AdminScanExclusionsComponent implements OnInit {
    private readonly translocoService = inject(TranslocoService);
    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;
    readonly TABLE_SCHEMA = TableWrapperComponent.createTableSchema<
        ComputedScanExclusion
    >({
        NAME: {
            label: this.translocoService.translate('adminScanExclusions.table.header.title'),
            id: 'name',
            type: 'property',
            widthClass: 'tw-w-auto',
        },
        ACTIONS: {
            label: this.translocoService.translate('adminScanExclusions.table.header.actions'),
            id: 'actions',
            type: 'custom',
            actionColumn: true,
            widthClass: 'tw-w-20',
        },
    });

    @ViewChild(TableWrapperComponent) private readonly tableWrapper?: TableWrapperComponent<ComputedScanExclusion>;

    private menuTriggerElement: HTMLElement | null = null;

    modules: Module[] = [];
    activeModule: string = '';
    perPage = 0;
    page = 1;
    total = 0;
    loading = false;
    scanExclusions: ComputedScanExclusion[] = [];
    private destroy$ = new Subject<void>();

    constructor (
        private readonly paramService: ParamService,
        private readonly destroyRef: DestroyRef,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly scanExclusionsService: ScanExclusionsService,
        private readonly scanExclusionsRepo: ScanExclusionsRepo,
        private readonly accessibilityScanExclusionsRepo: AccessibilityScanExclusionsRepo,
        private readonly confirmationService: ConfirmationService,
        private readonly stateService: StateService,
        private readonly adminLayoutService: AdminLayoutService,
    ) {}

    ngOnInit (): void {
        this.setBreadcrumbs();
        const { page_size, page, module } = this.paramService.getParams();

        this.modules = this.scanExclusionsService.getModules();
        this.activeModule = module || this.scanExclusionsService.getDefaultModule();

        this.getScanExclusions(parseInt(page_size), parseInt(page), this.activeModule);

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((params) => {
            this.getScanExclusions(params.page_size, params.page, params.module);
        });

        this.destroyRef.onDestroy(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            deregisterHook();
            this.destroy$.next();
            this.destroy$.complete();
        });
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: 'admin.scanExclusions.table.breadcrumbLabel',
                    state: 'base.customer.admin.scan-exclusions',
                },
            ],
        });
    }

    async getScanExclusions (pageSize: number, page: number, module: string): Promise<void> {
        const params = {
            page_size: pageSize || DEFAULT_PAGE_SIZE,
            page: page || 1,
        };
        this.loading = true;
        try {
            const scanExclusionsResponse = module === 'accessibility'
                ? await this.accessibilityScanExclusionsRepo.getAllAccessibilityScanExclusions(params)
                : await this.scanExclusionsRepo.getAllScanExclusions(params, module);

            this.perPage = scanExclusionsResponse.perPage || 0;
            this.page = scanExclusionsResponse.currentPage || 1;
            this.total = scanExclusionsResponse.total || 0;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            this.scanExclusions = scanExclusionsResponse.map(scanItem => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return {
                    ...scanItem,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    name: module === 'accessibility' ? scanItem.short_description : scanItem.title,
                    actionItems: [
                        {
                            label: this.translocoService.translate('adminScanExclusions.actions.editButtonLabel'),
                            icon: 'pi pi-pencil',
                            command: () => this.editScanExclusion(scanItem, module),
                        },
                        {
                            label: this.translocoService.translate('adminScanExclusions.actions.deleteButtonLabel'),
                            icon: 'pi pi-trash',
                            command: () => this.deleteScanExclusion(scanItem, module),
                        },
                    ] as MenuItem[],
                };
            });
        } catch {
            this.globalToastMessageService.error(
                this.translocoService.translate('adminScanExclusions.getScanExclusions.failedToast'),
            );
        } finally {
            this.loading = false;
        }
    }

    async reload (): Promise<void> {
        const { page_size, page, module } = this.paramService.getParams();
        return this.getScanExclusions(parseInt(page_size), parseInt(page), module);
    }

    onTabChange (module: string | number) {
        this.actionTriggerService.track({
            action: 'admin-scan-exclusions-tab-change',
        });
        const { page_size } = this.paramService.getParams();
        this.activeModule = String(module);
        this.paramService.updateParams({
            page: 1,
            page_size: page_size,
            module: this.activeModule,
        });
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.paramService.updateParams({
            page: pageNumber,
            page_size: rows,
            module: this.activeModule,
        });
    }

    addScanExclusion (): void {
        this.actionTriggerService.track({ action: 'admin-scan-exclusions-add-scan-btn' });
        this.stateService.go('base.customer.admin.add-scan-exclusions');
    }

    editScanExclusion (scanExclusion: ScanExclusion, module: string): void {
        this.actionTriggerService.track({ action: 'admin-scan-exclusions-edit-scan-btn' });
        this.stateService.go('base.customer.admin.edit-scan-exclusions', {
            id: scanExclusion.id,
            module,
        });
    }

    public onMenuHide (): void {
        const el = document.activeElement;
        this.menuTriggerElement = (el instanceof HTMLElement) ? el : null;
    }

    deleteScanExclusion (scanExclusion: ScanExclusion, module: string): void {
        this.tableWrapper?.trackAction(this.scanExclusions, this.scanExclusions.find(e => e.id === scanExclusion.id) ?? scanExclusion as unknown as ComputedScanExclusion, 'delete', () => true);
        this.actionTriggerService.track({
            action: 'admin-scan-exclusions-delete-scan-btn',
        });

        const scanExclusionName = 'short_description' in scanExclusion ? scanExclusion.short_description : scanExclusion.title;

        this.confirmationService.confirm({
            header: this.translocoService.translate('adminScanExclusions.deleteDialog.header'),
            message: this.translocoService.translate('adminScanExclusions.deleteDialog.confirmMessage{{scanExclusionName}}',
                { scanExclusionName: scanExclusionName }),
            closable: true,
            closeOnEscape: true,
            dismissableMask: true,
            rejectButtonProps: {
                label: this.translocoService.translate('adminScanExclusions.deleteDialog.cancelBtnLabel'),
                severity: 'secondary',
                variant: 'text',
            },
            acceptButtonProps: {
                label: this.translocoService.translate('adminScanExclusions.deleteDialog.deleteBtnLabel'),
                severity: 'danger',
            },
            reject: () => {
                this.tableWrapper?.focusService?.reset();
                this.menuTriggerElement?.focus();
                this.menuTriggerElement = null;
            },
            accept: async () => {
                this.loading = true;
                if (module === 'accessibility') {
                    await this.accessibilityScanExclusionsRepo.destroyAccessibilityScanExclusions(scanExclusion.id);
                } else {
                    await this.scanExclusionsRepo.destroyScanExclusions(scanExclusion.id);
                }
                this.globalToastMessageService.info(
                    this.translocoService.translate('adminScanExclusions.deleteDialog.deleteSuccess{{scanExclusionName}}', {
                        scanExclusionName: scanExclusionName,
                    }),
                );
                await this.reload();
            },
        });
    }

    protected readonly pageSize = DEFAULT_PAGE_SIZE;
}
